import { calcLeakRate, type ApplianceType } from './leak-rate.js'
import type { PdfSection } from './pdf.js'

export type { ApplianceType }

export interface RefrigerantLogData {
    // Company
    companyName:string;
    technicianName:string;
    epaCertNumber?:string;
    branch?:string;
    phone?:string;
    email?:string;
    // Customer/Site
    customerName:string;
    siteName?:string;
    address?:string;
    equipmentLocation?:string;
    equipmentId?:string;
    // Refrigerant
    refrigerantType:string;
    addedLbs:number;
    recoveredLbs?:number;
    fullChargeLbs:number;
    // Leak Rate
    dateCurrentService:string;
    datePreviousAddition:string;
    applianceType:ApplianceType;
    // Service Notes
    leakLocation?:string;
    repairsPerformed?:string;
    componentsReplaced?:string;
    followUp?:string;
    additionalNotes?:string;
}

function opt (value:string|undefined):string {
    return value && value.trim() ? value.trim() : 'N/A'
}

export function daysBetween (dateA:string, dateB:string):number {
    const a = new Date(dateA).getTime()
    const b = new Date(dateB).getTime()
    return Math.max(1, Math.abs(Math.round((a - b) / (1000 * 60 * 60 * 24))))
}

export function buildRefrigerantLogPdf (
    data:RefrigerantLogData
):PdfSection[] {
    const sections:PdfSection[] = []

    sections.push({
        heading: 'Company',
        rows: [
            { label: 'Company', value: data.companyName },
            { label: 'Technician', value: data.technicianName },
            { label: 'EPA Cert #', value: opt(data.epaCertNumber) },
            { label: 'Branch', value: opt(data.branch) },
            { label: 'Phone', value: opt(data.phone) },
            { label: 'Email', value: opt(data.email) },
        ],
    })

    sections.push({
        heading: 'Customer / Site',
        rows: [
            { label: 'Customer', value: data.customerName },
            { label: 'Site Name', value: opt(data.siteName) },
            { label: 'Address', value: opt(data.address) },
            {
                label: 'Equipment Location',
                value: opt(data.equipmentLocation),
            },
            { label: 'Equipment ID', value: opt(data.equipmentId) },
        ],
    })

    sections.push({
        heading: 'Refrigerant',
        rows: [
            { label: 'Refrigerant', value: data.refrigerantType },
            { label: 'Added (lbs)', value: String(data.addedLbs) },
            {
                label: 'Recovered (lbs)',
                value: data.recoveredLbs !== undefined ?
                    String(data.recoveredLbs) : 'N/A',
            },
            { label: 'Full Charge (lbs)', value: String(data.fullChargeLbs) },
        ],
    })

    const days = daysBetween(data.dateCurrentService, data.datePreviousAddition)
    const lr = calcLeakRate({
        applianceType: data.applianceType,
        refrigerantAdded: data.addedLbs,
        fullSystemCharge: data.fullChargeLbs,
        daysSinceLast: days,
    })

    const statusText =
        lr.exceedsThreshold === null ?
            'N/A (no threshold for this appliance type)' :
            lr.exceedsThreshold ?
                `EXCEEDS threshold (${lr.threshold}%)` :
                `Compliant (threshold: ${lr.threshold}%)`

    sections.push({
        heading: 'Leak Rate',
        rows: [
            {
                label: 'Current Service Date',
                value: data.dateCurrentService,
            },
            {
                label: 'Previous Addition Date',
                value: data.datePreviousAddition,
            },
            { label: 'Days Since Last Addition', value: String(days) },
            { label: 'Appliance Type', value: data.applianceType },
            {
                label: 'Annualized Leak Rate',
                value: `${lr.leakRatePercent.toFixed(1)}%`,
            },
            { label: 'Status', value: statusText },
        ],
    })

    sections.push({
        heading: 'Service Notes',
        rows: [
            { label: 'Leak Location', value: opt(data.leakLocation) },
            {
                label: 'Repairs Performed',
                value: opt(data.repairsPerformed),
            },
            {
                label: 'Components Replaced',
                value: opt(data.componentsReplaced),
            },
            { label: 'Follow-up', value: opt(data.followUp) },
            {
                label: 'Additional Notes',
                value: opt(data.additionalNotes),
            },
        ],
    })

    return sections
}
